/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import React from 'react';
import { Button } from 'tc-client-common';
import * as consts from '../../const.js';
import { path } from 'ramda';
import PropTypes from 'prop-types';

class ExportArchiveWithProgressDisplay extends React.Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.state = {
      percentage: '0',
      status: null,
      delay: 3000,
      study: null,
      downloadDisable: false
    };

    this.downloadArchive = this.downloadArchive.bind(this);
    this.cancelDownloadArchive = this.cancelDownloadArchive.bind(this);
    this.getProgress = this.getProgress.bind(this);
    this.cancelExportArchive = this.cancelExportArchive.bind(this);
    this.stopExport = this.stopExport.bind(this);
    this.initiateExportArchive = this.initiateExportArchive.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    if (!this.props.exportStudy) {
      this.props
        .fetchStudies(this.props.user.userId, this.props.sponsor)
        .then((response) => {
          if (this._isMounted) {
            //temporary solution, new endpoint will be added to see for which study there is an export in progress
            const study = path(['data', 'studies', 'data', 0, 'study'], response);
            this.setState(() => ({ study: study }));
            if (study) {
              this.getProgress(study.gid, this.props.user.username);
            }
          }
        })
        .catch((error) => console.error(error));
    } else {
      this.getProgress(this.props.exportStudy.gid, this.props.user.username);
      this.setState(() => ({ study: this.props.exportStudy }));
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const shouldStartFetchingProgress =
      this.props.isExportInitiated &&
      prevProps.isExportInitiated !== this.props.isExportInitiated &&
      this.state.study &&
      this.state.status !== 'COMPLETED';

    const shouldRestartStatus =
      !prevProps.isExportInitiated && prevProps.isExportInitiated !== this.props.isExportInitiated;

    if (shouldRestartStatus) {
      this.setState(() => ({ status: 'STARTED' }));
    }

    if (shouldStartFetchingProgress) {
      this.interval = setInterval(() => {
        this.getProgress(this.state.study.gid, this.props.user.username);
      }, this.state.delay);
    }

    if (this.state.status !== 'STARTED' && this.state.percentage !== 0) {
      this.setState(() => ({ percentage: 0 }));
    } else if (this.state.status === 'STARTED' && !this.props.isExportInitiated) {
      this.props.initiatedExportArchive(this.state.study);
    } else if (this.state.status === 'COMPLETED') {
      this.stopExport();
    } else if (this.state.status === 'FAILED' || this.state.status === 'NOTIFICATION_FAILED') {
      this.stopExport();
    }

    if (this.props.exportStudy && prevProps.exportStudy !== this.props.exportStudy) {
      this.setState(() => ({ study: this.props.exportStudy }));
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    clearTimeout(this.statusFetchTimeout);
    this.interval = null;
    this._isMounted = false;
  }

  downloadArchive({ studyGid, username, studyProtocolNumber, sponsor }) {
    this.setState(() => ({ downloadDisable: true }));
    this.props
      .downloadArchive({ studyGid, username, studyProtocolNumber, sponsor })
      .then((response) => {
        if (this._isMounted) {
          this.setState(() => ({ status: 'CONCLUDED' }));
          this.setState(() => ({ downloadDisable: false }));
          this.props.resetIntiatedExportArchive();
          this.statusFetchTimeout = setTimeout(() => this.props.getBatchArchivingStatus(studyGid), 500);
        }
      })
      .catch((error) => {
        console.error(error);
        this.setState(() => ({ downloadDisable: false }));
      });
  }

  stopExport() {
    clearInterval(this.interval);
    this.props.resetIntiatedExportArchive();
  }

  getProgress(studyGid, username) {
    this.props
      .getProgress(studyGid, username)
      .then((response) => {
        if (this._isMounted) {
          this.setState(() => ({
            percentage: path(['data', 'BatchJobProgress', 'percentage'], response),
            status: path(['data', 'BatchJobProgress', 'status'], response)
          }));
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          this.setState(() => ({ status: 'FORBIDDEN' }));
        } else if (this._isMounted) {
          this.setState(() => ({ status: 'FAILED' }));
          clearInterval(this.interval);
        }
      });
  }

  cancelDownloadArchive({ studyGid, username, studyProtocolNumber, sponsor }) {
    this.props
      .cancelDownloadArchive({ studyGid, username, studyProtocolNumber, sponsor })
      .then((response) => {
        if (this._isMounted) {
          this.setState(() => ({ status: 'ABORTED', percentage: 0 }));
          this.stopExport();
        }
      })
      .catch((error) => {
        if (this._isMounted) {
          this.setState(() => ({ status: 'ABORTED', percentage: 0 }));
          this.stopExport();
        }
        console.error(error);
      });
  }

  cancelExportArchive({ studyGid, username, studyProtocolNumber, sponsor }) {
    this.stopExport();
    this.setState(() => ({ status: null, percentage: 0 }));
    this.props.cancelExportArchive({ studyGid, username, studyProtocolNumber, sponsor }).catch((error) => {
      console.error(error);
    });
  }

  showProgressBar(status, isExportInitiated) {
    if (isExportInitiated && status !== 'COMPLETED' && status !== 'FAILED' && status !== 'NOTIFICATION_FAILED') {
      return true;
    }
  }
  initiateExportArchive(study, username) {
    this.setState(() => ({ status: null, percentage: 0 }));
    this.props.initiateExportArchive(study, username);
  }

  render() {
    const styles = {
      width: this.state.percentage + '%'
    };

    const { user, sponsor, isExportInitiated, exportStudy } = this.props;
    const username = path(['username'], user);
    const exportStudyGid = path(['gid'], this.state.study);
    const exportProtocolNumber = path(['protocolNumber'], this.state.study);
    return (
      <div>
        {this.showProgressBar(this.state.status, isExportInitiated) && (
          <div className="progress-container">
            <div className={`bar ${this.state.percentage > 90 ? 'over-90' : ''}`} style={styles}>
              <span className="progress-tooltip">{`${this.state.percentage}%`}</span>
            </div>
            {this.state.status === 'STARTED' && (
              <div className="buttons-container">
                <Button
                  label={consts.CANCEL_EXPORT}
                  theme="default xs-button cancel-btn"
                  handleClick={() =>
                    this.cancelExportArchive({
                      sponsor,
                      studyGid: exportStudyGid,
                      username,
                      studyProtocolNumber: exportProtocolNumber
                    })
                  }
                />
              </div>
            )}
          </div>
        )}
        {this.state.status === 'COMPLETED' && (
          <div className="export-complete-bar">
            {consts.EXPORT_COMPLETED}
            <Button
              label={consts.DOWNLOAD}
              disabled={this.state.downloadDisable}
              theme="active xs-button download-btn"
              handleClick={() =>
                this.downloadArchive({
                  studyGid: exportStudyGid,
                  username,
                  studyProtocolNumber: exportProtocolNumber,
                  sponsor
                })
              }
            />
            <Button
              label={consts.CANCEL_DOWNLOAD}
              theme="default xs-button cancel-btn"
              disabled={this.state.downloadDisable}
              handleClick={() =>
                this.cancelDownloadArchive({
                  studyGid: exportStudyGid,
                  username,
                  studyProtocolNumber: exportProtocolNumber,
                  sponsor
                })
              }
            />
          </div>
        )}
        {(this.state.status === 'FAILED' || this.state.status === 'NOTIFICATION_FAILED') && (
          <div className="export-failed-bar">
            {consts.EXPORT_FAILED}
            <Button
              label={consts.RETRY}
              theme="active xs-button retry-btn"
              handleClick={() => this.initiateExportArchive(this.state.study, username)}
            />
            <Button
              label={consts.CANCEL_BTN}
              theme="default xs-button"
              handleClick={() =>
                this.cancelExportArchive({
                  sponsor,
                  studyGid: exportStudyGid,
                  username,
                  studyProtocolNumber: exportProtocolNumber
                })
              }
            />
          </div>
        )}
      </div>
    );
  }
}

ExportArchiveWithProgressDisplay.propTypes = {
  user: PropTypes.object,
  sponsor: PropTypes.string,
  isExportInitiated: PropTypes.bool,
  downloadArchive: PropTypes.func,
  cancelDownloadArchive: PropTypes.func,
  getProgress: PropTypes.func,
  fetchStudies: PropTypes.func,
  initiateExportArchive: PropTypes.func,
  initiatedExportArchive: PropTypes.func,
  resetIntiatedExportArchive: PropTypes.func,
  cancelExportArchive: PropTypes.func,
  getBatchArchivingStatus: PropTypes.func
};

export default ExportArchiveWithProgressDisplay;
